import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface ParentUser {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  phone?: string;
  club_id?: string;
}

interface ParentAuthContextType {
  user: User | null;
  session: Session | null;
  parentUser: ParentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: { full_name?: string; phone?: string; email?: string }) => Promise<{ error: any }>;
  isParent: boolean;
}

const ParentAuthContext = createContext<ParentAuthContextType | undefined>(undefined);

export function ParentAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [parentUser, setParentUser] = useState<ParentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchParentUser = async (userId: string) => {
    try {
      // Use RPC to get parent info
      const { data, error } = await supabase
        .rpc('get_current_parent')
        .single();

      if (error) {
        console.error('Error fetching parent user:', error);
        return null;
      }

      return data as ParentUser;
    } catch (error) {
      console.error('Error in fetchParentUser:', error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Defer async operations with setTimeout to avoid deadlock
        if (newSession?.user) {
          setTimeout(() => {
            fetchParentUser(newSession.user.id).then((parentData) => {
              setParentUser(parentData);
              setLoading(false);
            });
          }, 0);
        } else {
          setParentUser(null);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession?.user) {
        fetchParentUser(existingSession.user.id).then((parentData) => {
          setParentUser(parentData);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setParentUser(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/parent/reset-password`,
    });
    return { error };
  };

  const updateProfile = async (updates: { full_name?: string; phone?: string; email?: string }) => {
    if (!parentUser) {
      return { error: { message: 'Not authenticated' } };
    }

    try {
      // Update email in auth.users if email is being changed
      if (updates.email && updates.email !== parentUser.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: updates.email,
        });
        if (authError) {
          return { error: authError };
        }
      }

      // Update parent profile in parents table
      const { error: updateError } = await supabase
        .from('parents')
        .update({
          full_name: updates.full_name,
          phone: updates.phone,
          email: updates.email,
        })
        .eq('id', parentUser.id);

      if (updateError) {
        return { error: updateError };
      }

      // Refresh parent user data
      const updatedParent = await fetchParentUser(user!.id);
      if (updatedParent) {
        setParentUser(updatedParent);
      }

      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const isParent = !!parentUser && !!parentUser.user_id;

  const value = {
    user,
    session,
    parentUser,
    loading,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    isParent,
  };

  return (
    <ParentAuthContext.Provider value={value}>
      {children}
    </ParentAuthContext.Provider>
  );
}

export function useParentAuth() {
  const context = useContext(ParentAuthContext);
  if (context === undefined) {
    throw new Error('useParentAuth must be used within a ParentAuthProvider');
  }
  return context;
}
